import { CollapsibleFilters } from '../common/CollapsibleFilters';
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import { getApiErrorMessage } from "../../lib/apiError";
import {
  ACTIVE_CONTENT_STATUSES,
  contentPages,
  type ContentArticle,
} from "../../lib/contentApi";
import { useArticleGeneration } from "../../hooks/useArticleGeneration";

export function ArticleGenerator() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const { retryGeneration } = useArticleGeneration();
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const query = useQuery({
    queryKey: ["articles", term, status],
    queryFn: () => {
      const params = new URLSearchParams({ search: term });
      if (status === "active") params.set("in_progress", "true");
      else if (status !== "all") params.set("status", status);
      return contentPages<ContentArticle>(`/api/marketing/articles/?${params}`);
    },
    refetchInterval: (query) =>
      query.state.data?.some((a) => ACTIVE_CONTENT_STATUSES.includes(a.status))
        ? 5000
        : false,
  });
  const owners = useQuery({
    queryKey: ["content-owners"],
    queryFn: async () =>
      (
        await api.get<
          Array<{
            id: number;
            first_name: string;
            last_name: string;
            email: string;
          }>
        >("/api/marketing/articles/owners/")
      ).data,
  });
  const update = useMutation({
    mutationFn: ({ id, ...data }: Partial<ContentArticle> & { id: number }) =>
      api.patch(`/api/marketing/articles/${id}/`, data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["articles"] }),
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not update the plan")),
  });
  const articles = query.data || [];
  const pageCount = Math.max(1, Math.ceil(articles.length / 20));
  const currentPage = Math.min(page, pageCount);
  const groups =
    view === "board"
      ? ["draft", "review", "ready"]
      : view === "calendar"
        ? [...new Set(articles.map((a) => a.due_date || "Unscheduled"))].sort()
        : ["Articles"];
  const card = (article: ContentArticle) => (
    <Card key={article.id} variant="outlined" sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "start", justifyContent: "space-between" }}
      >
        <Typography
          level="title-md"
          component={Link}
          to={`/marketing/article/${article.id}`}
          sx={{ overflowWrap: "anywhere" }}
        >
          {article.title}
        </Typography>
        <Chip
          size="sm"
          color={
            article.status === "error"
              ? "danger"
              : article.status === "published"
                ? "success"
                : "neutral"
          }
        >
          {article.status === "content_done"
            ? "Ready to publish"
            : article.status.replaceAll("_", " ")}
        </Chip>
      </Stack>
      {ACTIVE_CONTENT_STATUSES.includes(article.status) && (
        <LinearProgress
          determinate
          value={article.progress}
          aria-label="Generation progress"
        />
      )}
      {article.status === "error" && (
        <Alert color="danger">
          {article.error_message || "Generation failed"}
          <Button size="sm" onClick={() => retryGeneration(article.id)}>
            Retry
          </Button>
        </Alert>
      )}
      <Stack
        direction={view === "board" ? "column" : { xs: "column", sm: "row" }}
        spacing={1}
      >
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Editorial stage</FormLabel>
          <Select
            value={article.editorial_stage}
            disabled={update.isPending}
            onChange={(_, value) => {
              if (value)
                update.mutate({ id: article.id, editorial_stage: value });
            }}
          >
            {["draft", "review", "ready"].map((stage) => (
              <Option key={stage} value={stage}>
                {stage[0].toUpperCase() + stage.slice(1)}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 0 }}>
          <FormLabel>Owner</FormLabel>
          <Select
            value={article.editorial_owner || 0}
            disabled={update.isPending || owners.isLoading}
            onChange={(_, value) =>
              update.mutate({ id: article.id, editorial_owner: value || null })
            }
          >
            <Option value={0}>Unassigned</Option>
            {owners.data?.map((owner) => (
              <Option key={owner.id} value={owner.id}>
                {`${owner.first_name} ${owner.last_name}`.trim() || owner.email}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 0 }}>
          <FormLabel>Due date</FormLabel>
          <Input
            type="date"
            value={article.due_date || ""}
            disabled={update.isPending}
            onChange={(e) =>
              update.mutate({
                id: article.id,
                due_date: e.target.value || null,
              })
            }
          />
        </FormControl>
      </Stack>
    </Card>
  );
  return (
    <Stack spacing={2}>
      <CollapsibleFilters><Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Input
          placeholder="Search articles or keywords"
          aria-label="Search articles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Select
          aria-label="Generation status"
          value={status}
          onChange={(_, value) => {
            setStatus(value || "all");
            setPage(1);
          }}
        >
          {[
            ["all", "All statuses"],
            ["active", "In progress"],
            ["content_done", "Ready to publish"],
            ["published", "Published"],
            ["error", "Failed"],
          ].map(([value, label]) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Select>
        <Select
          aria-label="Planning view"
          value={view}
          onChange={(_, value) => setView(value || "list")}
        >
          <Option value="list">Article list</Option>
          <Option value="board">Editorial board</Option>
          <Option value="calendar">Calendar agenda</Option>
        </Select>
        <Button variant="outlined" onClick={() => void query.refetch()}>
          Refresh
        </Button>
      </Stack></CollapsibleFilters>
      <Typography level="body-sm">
        {articles.length} articles · Editorial stages and due dates organize
        work; they do not publish content.
      </Typography>
      {query.isPending && <LinearProgress aria-label="Loading articles" />}
      {query.isError && (
        <Alert color="danger">
          {getApiErrorMessage(query.error, "Could not load articles")}
          <Button onClick={() => void query.refetch()}>Retry</Button>
        </Alert>
      )}
      {!query.isPending && !query.isError && !articles.length && (
        <Alert>
          No articles match. Create an article from a topical-map idea to get
          started.
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            view === "board"
              ? { xs: "1fr", lg: "repeat(3,minmax(0,1fr))" }
              : "1fr",
          gap: 2,
        }}
      >
        {groups.map((group) => (
          <Stack spacing={1.5} key={group}>
            {view !== "list" && (
              <Typography level="title-lg">
                {group === "Unscheduled"
                  ? group
                  : view === "calendar"
                    ? new Date(`${group}T12:00:00`).toLocaleDateString(
                        undefined,
                        { month: "long", day: "numeric", year: "numeric" },
                      )
                    : group[0].toUpperCase() + group.slice(1)}
              </Typography>
            )}
            {(view === "list"
              ? articles.slice((currentPage - 1) * 20, currentPage * 20)
              : articles.filter((a) =>
                  view === "board"
                    ? a.editorial_stage === group
                    : (a.due_date || "Unscheduled") === group,
                )
            ).map(card)}
          </Stack>
        ))}
      </Box>
      {view === "list" && articles.length > 20 && (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Button
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </Button>
          <Typography>
            Page {currentPage} of {pageCount}
          </Typography>
          <Button
            disabled={currentPage === pageCount}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
